import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { consultaDocumentoConcluida } from './documentoNotificacao/actions';

import type { AggregatedIssue as PreValidationIssue } from '@/shared/components/ui/common';
import type { ManifestacaoChatPaginado } from '@/features/extrajudicial/types/manifestacaoChatUi.types';
import type { NotificacaoStatusPayload } from '@/features/extrajudicial/types/notificacaoStatusPayload.types';
import type {
  EnvioDigitalSnapshotOutput,
  EnvioFisicoOutput,
} from '@/features/extrajudicial/types/envio.types';
import type {
  ItemEditData,
  ItemWithRelations,
  LoteCustos,
  LoteDashboard,
  LoteDetailResponse,
  LoteFormState,
  LoteListItem,
  LoteOutput,
  LotePerformanceDetailedResponse,
  LoteStats,
  LoteTabCounts,
  ManifestacaoChatResponse,
  NotificacaoListItem,
  NotificacaoStats,
  StartNegociacaoBulkResult,
  TemplateItem,
  ValidationErrorSample,
  ValidationResult,
  ValoresEfetivos,
  WorkflowStep,
} from '@/features/extrajudicial/types';
import { SendMethod } from '@/features/extrajudicial/types';
import type { PaginationMeta } from '@/features/extrajudicial/types/common.types';
import type { AjusteLoteGrupo } from '@/features/extrajudicial/types/ajustes.types';
import { ClassificacaoItem } from '@/features/extrajudicial/types/common.types';
import {
  getStatusNotificacaoLabel,
  StatusCorreios,
  StatusEmail,
  StatusNotificacao,
  StatusWhatsApp,
} from '@/features/extrajudicial/types/status.types';

import {
  abrirNegociacaoEmLote,
  agendarLote,
  arquivarItem,
  encerrarItem,
  atualizarConfiguracaoLote,
  atualizarItem,
  avancarConfiguracaoLote,
  baixarModeloCsv,
  calcularCustos,
  calcularCustosLote,
  cancelarConfiguracaoLote,
  cancelarItem,
  cancelarLote,
  confirmarEnvio,
  criarIndividual,
  criarLote,
  criarLoteComMapeamento,
  contarLotesRascunho,
  criarLoteRascunho,
  enviarDigital,
  enviarFisico,
  enviarLoteAgora,
  reenviarItem,
  reenviarLote,
  listarItensDoLote,
  listarAjustes,
  listarLotes,
  listarLotesRascunho,
  listarNotificacoes,
  listarTemplates,
  obterEstatisticas,
  obterEntregasPorCanal,
  obterInfoCancelamentoItem,
  obterItem,
  obterItemParaEdicao,
  obterItemPorId,
  obterLote,
  obterLotePerformance,
  obterLoteSummary,
  obterLoteTabCounts,
  obterMetodosPermitidos,
  obterNotificacoesStats,
  obterSnapshotEnvioDigital,
  obterTempoRestanteLote,
  obterValoresEfetivos,
  removerItemDaValidacao,
  enviarRespostaSimples,
  marcarManifestacaoLida,
  obterManifestacaoChat,
  obterManifestacaoChatAnteriores,
  restaurarItem,
  validarCpfCnpj,
  validarParaEnvio,
} from './actions';

const statusNotificacaoValues = Object.values(StatusNotificacao) as readonly StatusNotificacao[];

const isStatusNotificacao = (status: unknown): status is StatusNotificacao => {
  return statusNotificacaoValues.includes(status as StatusNotificacao);
};

const statusEmailValues = Object.values(StatusEmail) as readonly StatusEmail[];

const parseStatusEmail = (status: unknown): StatusEmail | null => {
  if (statusEmailValues.includes(status as StatusEmail)) {
    return status as StatusEmail;
  }
  return null;
};

const statusWhatsAppValues = Object.values(StatusWhatsApp) as readonly StatusWhatsApp[];

const parseStatusWhatsApp = (status: unknown): StatusWhatsApp | null => {
  if (statusWhatsAppValues.includes(status as StatusWhatsApp)) {
    return status as StatusWhatsApp;
  }
  return null;
};

const statusCorreiosValues = Object.values(StatusCorreios) as readonly StatusCorreios[];

const parseStatusCorreios = (status: unknown): StatusCorreios | null => {
  if (statusCorreiosValues.includes(status as StatusCorreios)) {
    return status as StatusCorreios;
  }
  return null;
};

interface ExtrajudicialState {
  form: LoteFormState;
  templates: TemplateItem[];
  isLoadingTemplates: boolean;
  isDownloadingTemplate: boolean;
  error: string | null;

  itemEditData: ItemEditData | null;
  isLoadingItemEdit: boolean;
  isUpdatingItem: boolean;
  currentStep: WorkflowStep;
  scheduleMode: 'now' | 'schedule';
  scheduledDate: string;
  scheduledTime: string;
  isUploading: boolean;
  uploadErrors: ValidationErrorSample[] | null;
  preValidationIssues: PreValidationIssue[] | null;
  preValidationHasCritical: boolean;
  preValidationTotalRows: number | null;
  loteId: string | null;
  validationResult: ValidationResult | null;

  isValidatingDoc: boolean;
  docLookupCurrent: string | null;
  docLookupError: string | null;
  docValidationData: {
    tipo?: 'CPF' | 'CNPJ';
    documento?: string;
    nome: string;
    fonte?: 'database' | 'api';
    logradouro?: string;
    numero?: string;
    complemento?: string;
    bairro?: string;
    cidade?: string;
    uf?: string;
    cep?: string;
  } | null;

  lotes: LoteListItem[];
  lotesPagination: PaginationMeta | null;
  lotesDashboard: LoteDashboard | null;
  isLoadingLotes: boolean;
  lotesError: string | null;
  lotesRequestId: string | null;
  lotesRascunho: LoteListItem[];
  isLoadingLotesRascunho: boolean;
  lotesRascunhoError: string | null;
  totalLotesRascunho: number;
  ajustesGrupos: AjusteLoteGrupo[];
  ajustesEscopo: string | null;
  isLoadingAjustes: boolean;
  ajustesError: string | null;

  loteAtual: LoteOutput | null;
  loteDetail: LoteDetailResponse | null;
  isLoadingLote: boolean;
  lotePerformance: LotePerformanceDetailedResponse | null;
  isLoadingLotePerformance: boolean;

  stats: LoteStats | null;
  isLoadingStats: boolean;

  itensDoLote: ItemWithRelations[];
  itensPagination: PaginationMeta | null;
  isLoadingItens: boolean;

  notificacoes: NotificacaoListItem[];
  notificacoesPagination: PaginationMeta | null;
  isLoadingNotificacoes: boolean;
  notificacoesError: string | null;
  notificacoesStats: NotificacaoStats | null;
  isLoadingNotificacoesStats: boolean;
  loteTabCounts: LoteTabCounts | null;
  isLoadingLoteTabCounts: boolean;

  itemAtual: ItemWithRelations | null;
  isLoadingItem: boolean;
  entregasPanel: {
    data: ItemWithRelations | null;
    isLoading: boolean;
    error: boolean;
  };
  digitalSendSnapshot: EnvioDigitalSnapshotOutput | null;
  isLoadingDigitalSendSnapshot: boolean;

  isEnviando: boolean;

  isAgendando: boolean;
  isCancelando: boolean;
  isEncerrando: boolean;
  isArquivando: boolean;
  isRestaurando: boolean;
  isAbrindoNegociacaoEmLote: boolean;
  negociacaoEmLoteResult: StartNegociacaoBulkResult | null;
  schedulingInfo: {
    secondsRemaining: number;
    canCancel: boolean;
    canEdit: boolean;
    sendAfter: string | null;
    status: string;
    cancelType?: string;
  } | null;
  isLoadingSchedulingInfo: boolean;

  valoresEfetivos: ValoresEfetivos | null;
  isLoadingValores: boolean;
  custosLote: LoteCustos | null;
  isLoadingCustos: boolean;

  isCreatingDraft: boolean;
  isSavingConfig: boolean;
  isAdvancing: boolean;
  metodosPermitidos: string[];
  isLoadingMetodos: boolean;

  manifestacaoChat: ManifestacaoChatPaginado | null;
  manifestacaoChatItemId: string | null;
  manifestacaoChatRequestId: string | null;
  isLoadingManifestacaoChat: boolean;
  erroManifestacaoChat: string | null;
  manifestacaoChatAnterioresRequestId: string | null;
  isCarregandoAnterioresManifestacaoChat: boolean;
  erroAnterioresManifestacaoChat: string | null;
  isEnviandoRespostaSimples: boolean;
}

const initialState: ExtrajudicialState = {
  form: {
    batchName: '',
    batchCode: '',
    whatsappTemplateId: '',
    emailTemplateId: '',
    fisicoTemplateId: '',
    sendMethod: '',
    prazoDias: 5,
  },
  templates: [],
  isLoadingTemplates: false,
  isDownloadingTemplate: false,
  error: null,

  itemEditData: null,
  isLoadingItemEdit: false,
  isUpdatingItem: false,
  currentStep: 'config',
  scheduleMode: 'now',
  scheduledDate: '',
  scheduledTime: '',
  isUploading: false,
  uploadErrors: null,
  preValidationIssues: null,
  preValidationHasCritical: false,
  preValidationTotalRows: null,
  loteId: null,
  validationResult: null,

  isValidatingDoc: false,
  docLookupCurrent: null,
  docLookupError: null,
  docValidationData: null,

  lotes: [],
  lotesPagination: null,
  lotesDashboard: null,
  isLoadingLotes: false,
  lotesError: null,
  lotesRequestId: null,
  lotesRascunho: [],
  isLoadingLotesRascunho: false,
  lotesRascunhoError: null,
  totalLotesRascunho: 0,
  ajustesGrupos: [],
  ajustesEscopo: null,
  isLoadingAjustes: false,
  ajustesError: null,

  loteAtual: null,
  loteDetail: null,
  isLoadingLote: false,
  lotePerformance: null,
  isLoadingLotePerformance: false,

  stats: null,
  isLoadingStats: false,

  itensDoLote: [],
  itensPagination: null,
  isLoadingItens: false,

  notificacoes: [],
  notificacoesPagination: null,
  isLoadingNotificacoes: false,
  notificacoesError: null,
  notificacoesStats: null,
  isLoadingNotificacoesStats: false,
  loteTabCounts: null,
  isLoadingLoteTabCounts: false,

  itemAtual: null,
  isLoadingItem: false,
  entregasPanel: {
    data: null,
    isLoading: false,
    error: false,
  },
  digitalSendSnapshot: null,
  isLoadingDigitalSendSnapshot: false,

  isEnviando: false,

  isAgendando: false,
  isCancelando: false,
  isEncerrando: false,
  isArquivando: false,
  isRestaurando: false,
  isAbrindoNegociacaoEmLote: false,
  negociacaoEmLoteResult: null,
  schedulingInfo: null,
  isLoadingSchedulingInfo: false,

  valoresEfetivos: null,
  isLoadingValores: false,
  custosLote: null,
  isLoadingCustos: false,

  isCreatingDraft: false,
  isSavingConfig: false,
  isAdvancing: false,
  metodosPermitidos: [],
  isLoadingMetodos: false,

  manifestacaoChat: null,
  manifestacaoChatItemId: null,
  manifestacaoChatRequestId: null,
  isLoadingManifestacaoChat: false,
  erroManifestacaoChat: null,
  manifestacaoChatAnterioresRequestId: null,
  isCarregandoAnterioresManifestacaoChat: false,
  erroAnterioresManifestacaoChat: null,
  isEnviandoRespostaSimples: false,
};

const MENSAGEM_DE_FALHA_DO_CHAT = 'Erro ao obter mensagens da manifestação';

const idTemporario = (requestId: string) => `temp-${requestId}`;

/** Troca de item, nova primeira página ou saída da tela: o que estava em voo deixa de valer. */
function reiniciarManifestacaoChat(
  state: ExtrajudicialState,
  itemId: string | null,
  requestId: string | null,
): void {
  state.manifestacaoChat = null;
  state.manifestacaoChatItemId = itemId;
  state.manifestacaoChatRequestId = requestId;
  state.erroManifestacaoChat = null;
  state.manifestacaoChatAnterioresRequestId = null;
  state.isCarregandoAnterioresManifestacaoChat = false;
  state.erroAnterioresManifestacaoChat = null;
}

/** O chat só recebe o que é do mesmo item: resposta ou envio de outro item nunca entra nele. */
const chatDoItem = (state: ExtrajudicialState, itemId: string): ManifestacaoChatPaginado | null =>
  state.manifestacaoChatItemId === itemId ? state.manifestacaoChat : null;

const chatDaPrimeiraPagina = (pagina: ManifestacaoChatResponse): ManifestacaoChatPaginado => ({
  mensagens: pagina.items,
  respostasSimplesSent: pagina.respostasSimplesSent,
  limiteRespostasSimples: pagina.limiteRespostasSimples,
  podeResponder: pagina.podeResponder,
  temNegociacaoAtiva: pagina.temNegociacaoAtiva,
  paginaCarregada: pagina.page,
  temAnteriores: pagina.hasNext,
  total: pagina.total,
});

/**
 * Mensagem nova que chegou depois da primeira página empurra as outras uma posição: a página
 * seguinte repete a última já carregada, por isso a deduplicação por id. As flags ficam as da tela,
 * que já contam o envio otimista.
 */
function prependerAnteriores(
  chat: ManifestacaoChatPaginado,
  page: number,
  pagina: ManifestacaoChatResponse,
): void {
  const carregadas = new Set(chat.mensagens.map((m) => m.id));
  const anteriores = pagina.items.filter((m) => !carregadas.has(m.id));
  chat.mensagens = [...anteriores, ...chat.mensagens];
  chat.paginaCarregada = page;
  chat.temAnteriores = pagina.hasNext;
  chat.total = pagina.total;
}

function atualizarPodeResponder(chat: ManifestacaoChatPaginado): void {
  chat.podeResponder =
    chat.respostasSimplesSent < chat.limiteRespostasSimples && !chat.temNegociacaoAtiva;
}

const resetFormState = (state: ExtrajudicialState) => {
  state.form = initialState.form;
  state.loteId = null;
  state.loteAtual = null;
  state.itemAtual = null;
  state.digitalSendSnapshot = null;
  state.isLoadingDigitalSendSnapshot = false;
  state.validationResult = null;
  state.uploadErrors = null;
  state.itensDoLote = [];
  state.itensPagination = null;
  state.currentStep = 'config';
  state.scheduleMode = 'now';
  state.scheduledDate = '';
  state.scheduledTime = '';
};

const aplicarConfiguracaoDoLote = (state: ExtrajudicialState, lote: LoteOutput) => {
  state.form.batchName = lote.batchName;
  state.form.batchCode = lote.batchCode;
  state.form.sendMethod = lote.sendMethod;
  state.form.whatsappTemplateId = lote.whatsappTemplateId ?? '';
  state.form.emailTemplateId = lote.emailTemplateId ?? '';
  state.form.fisicoTemplateId = lote.fisicoTemplateId ?? '';
};

const extrajudicialSlice = createSlice({
  name: 'extrajudicial',
  initialState,
  reducers: {
    setBatchName(state, action: PayloadAction<string>) {
      state.form.batchName = action.payload;
    },
    setBatchCode(state, action: PayloadAction<string>) {
      state.form.batchCode = action.payload;
    },
    setWhatsappTemplateId(state, action: PayloadAction<string>) {
      state.form.whatsappTemplateId = action.payload;
    },
    setEmailTemplateId(state, action: PayloadAction<string>) {
      state.form.emailTemplateId = action.payload;
    },
    setFisicoTemplateId(state, action: PayloadAction<string>) {
      state.form.fisicoTemplateId = action.payload;
    },
    setLotePrazoDias(state, action: PayloadAction<number>) {
      state.form.prazoDias = action.payload;
    },
    setSendMethod(state, action: PayloadAction<'' | SendMethod>) {
      const method = action.payload;
      state.form.sendMethod = method;

      if (method === SendMethod.EMAIL_APENAS) {
        state.form.whatsappTemplateId = '';
        state.form.fisicoTemplateId = '';
      } else if (method === SendMethod.WHATSAPP_APENAS) {
        state.form.emailTemplateId = '';
        state.form.fisicoTemplateId = '';
      } else if (method === SendMethod.SEDEX) {
        state.form.whatsappTemplateId = '';
        state.form.emailTemplateId = '';
      } else if (method === SendMethod.DIGITAL) {
        state.form.fisicoTemplateId = '';
      }
    },
    setScheduleMode(state, action: PayloadAction<'now' | 'schedule'>) {
      state.scheduleMode = action.payload;
      if (action.payload === 'now') {
        state.scheduledDate = '';
        state.scheduledTime = '';
      }
    },
    setScheduledDate(state, action: PayloadAction<string>) {
      state.scheduledDate = action.payload;
    },
    setScheduledTime(state, action: PayloadAction<string>) {
      state.scheduledTime = action.payload;
    },
    goToStep(state, action: PayloadAction<WorkflowStep>) {
      state.currentStep = action.payload;
    },
    clearUploadErrors(state) {
      state.uploadErrors = null;
    },
    clearPreValidation(state) {
      state.preValidationIssues = null;
      state.preValidationHasCritical = false;
      state.preValidationTotalRows = null;
    },
    setValidationResult(state, action: PayloadAction<ValidationResult | null>) {
      state.validationResult = action.payload;
    },
    clearDocValidationData(state) {
      state.docValidationData = null;
      state.isValidatingDoc = false;
      state.docLookupCurrent = null;
      state.docLookupError = null;
    },
    clearError(state) {
      state.error = null;
    },

    clearItemEditData(state) {
      state.itemEditData = null;
      state.isLoadingItemEdit = false;
      state.isUpdatingItem = false;
    },
    clearDigitalSendSnapshot(state) {
      state.digitalSendSnapshot = null;
      state.isLoadingDigitalSendSnapshot = false;
    },
    clearManifestacaoChat(state) {
      reiniciarManifestacaoChat(state, null, null);
      state.isLoadingManifestacaoChat = false;
    },
    resetForm(state) {
      resetFormState(state);
    },
    iniciarNovaNotificacao(
      state,
      action: PayloadAction<{ metodoPrefill?: string | null } | undefined>,
    ) {
      resetFormState(state);
      // Com método no prefill, o form fica '' e o efeito de prefill da página aplica o método:
      // gravá-lo aqui faria a troca a partir do valor antigo do store limpar o destinatário.
      if (!action.payload?.metodoPrefill) {
        state.form = { ...initialState.form, sendMethod: SendMethod.DIGITAL };
      }
    },
    resetState() {
      return initialState;
    },
    updateNotificacaoStatus(state, action: PayloadAction<NotificacaoStatusPayload>) {
      const { notificacaoId, status, updatedAt, enviosDigitais, envioFisico } = action.payload;
      const notificacao = state.notificacoes.find((n) => n.id === notificacaoId);

      if (notificacao) {
        notificacao.status = status;
        notificacao.statusLabel = isStatusNotificacao(status)
          ? getStatusNotificacaoLabel(status)
          : String(status);
        if (updatedAt) {
          notificacao.dataAtualizacao = updatedAt;
        }

        if (enviosDigitais) {
          notificacao.enviosDigitais = enviosDigitais.map((envio) => {
            const anterior = notificacao.enviosDigitais.find((e) => e.canal === envio.canal);
            return {
              canal: envio.canal,
              statusEmail: parseStatusEmail(envio.statusEmail),
              statusWhatsApp: parseStatusWhatsApp(envio.statusWhatsApp),
              destinatario: envio.destinatario ?? anterior?.destinatario ?? null,
              dataEnvio: envio.dataEnvio,
            };
          });
        }

        if (envioFisico !== undefined && envioFisico !== null) {
          const parsedStatusCorreios = parseStatusCorreios(envioFisico.statusCorreios);
          notificacao.envioFisico = {
            statusCorreios: parsedStatusCorreios ?? StatusCorreios.PENDENTE,
            codigoRastreio: envioFisico.codigoRastreio,
            custoEnvio: envioFisico.custoEnvio,
            scrybeCanCancelUntil: envioFisico.scrybeCanCancelUntil ?? null,
          };
        } else if (envioFisico === null) {
          notificacao.envioFisico = null;
        }
      }

      if (state.itemAtual && state.itemAtual.id === notificacaoId) {
        state.itemAtual.statusGeral = status;
        state.itemAtual.statusLabel = isStatusNotificacao(status)
          ? getStatusNotificacaoLabel(status)
          : String(status);
        if (updatedAt) {
          state.itemAtual.dataAtualizacao = updatedAt;
        }

        if (enviosDigitais) {
          if (state.itemAtual.enviosDigitais) {
            state.itemAtual.enviosDigitais = state.itemAtual.enviosDigitais.map((oldEnvio) => {
              const update = enviosDigitais.find((e) => e.canal === oldEnvio.canal);
              if (update) {
                const parsedStatusEmail = parseStatusEmail(update.statusEmail);
                const parsedStatusWhatsApp = parseStatusWhatsApp(update.statusWhatsApp);
                return {
                  ...oldEnvio,
                  statusEmail: parsedStatusEmail ?? oldEnvio.statusEmail,
                  statusWhatsApp: parsedStatusWhatsApp ?? oldEnvio.statusWhatsApp,
                  digitalSentAt: update.dataEnvio,
                };
              }
              return oldEnvio;
            });
          }
        }

        if (envioFisico !== undefined && envioFisico !== null) {
          if (state.itemAtual.envioFisico) {
            const parsedStatusCorreios = parseStatusCorreios(envioFisico.statusCorreios);
            state.itemAtual.envioFisico = {
              ...state.itemAtual.envioFisico,
              statusCorreios: parsedStatusCorreios ?? state.itemAtual.envioFisico.statusCorreios,
              trackingCode: envioFisico.codigoRastreio,
              custoEnvio: envioFisico.custoEnvio,
              scrybeCanCancelUntil:
                envioFisico.scrybeCanCancelUntil ||
                state.itemAtual.envioFisico.scrybeCanCancelUntil,
            };
          } else {
            const parsedStatusCorreios = parseStatusCorreios(envioFisico.statusCorreios);
            state.itemAtual.envioFisico = {
              id: '',
              empresaId: action.payload.empresaId,
              escritorioId: '',
              itemId: notificacaoId,
              provider: 'SCRYBE',
              pdfPath: '',
              pdfUrl: '',
              jobId: null,
              trackingCode: envioFisico.codigoRastreio,
              custoEnvio: envioFisico.custoEnvio,
              statusCorreios: parsedStatusCorreios ?? StatusCorreios.PENDENTE,
              statusLog: [],
              dataCriacao: new Date().toISOString(),
              dataAtualizacao: updatedAt || new Date().toISOString(),
              physicalPostedAt: null,
              physicalDeliveredAt: null,
              scrybeCanCancelUntil: envioFisico.scrybeCanCancelUntil || null,
            } satisfies EnvioFisicoOutput;
          }
        } else if (envioFisico === null) {
          state.itemAtual.envioFisico = null;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(consultaDocumentoConcluida, (state, { payload }) => {
      if (state.itemAtual?.numeroNotificacao === payload.numeroNotificacao && payload.documento) {
        state.itemAtual.documentoNotificacao = payload.documento;
      }
    });
    builder
      .addCase(obterItemParaEdicao.pending, (state) => {
        state.isLoadingItemEdit = true;
        state.error = null;
      })
      .addCase(obterItemParaEdicao.fulfilled, (state, action) => {
        state.isLoadingItemEdit = false;
        state.itemEditData = action.payload;
      })
      .addCase(obterItemParaEdicao.rejected, (state, action) => {
        state.isLoadingItemEdit = false;
        state.error = action.payload || 'Erro ao obter item para edição';
      })

      .addCase(atualizarItem.pending, (state) => {
        state.isUpdatingItem = true;
        state.error = null;
      })
      .addCase(atualizarItem.fulfilled, (state) => {
        state.isUpdatingItem = false;
      })
      .addCase(atualizarItem.rejected, (state, action) => {
        state.isUpdatingItem = false;
        state.error = action.payload || 'Erro ao salvar item';
      })

      .addCase(listarTemplates.pending, (state) => {
        state.isLoadingTemplates = true;
        state.error = null;
      })
      .addCase(listarTemplates.fulfilled, (state, action) => {
        state.isLoadingTemplates = false;
        state.templates = action.payload;
      })
      .addCase(listarTemplates.rejected, (state, action) => {
        state.isLoadingTemplates = false;
        state.error = action.payload || 'Erro ao listar templates';
      })

      .addCase(baixarModeloCsv.pending, (state) => {
        state.isDownloadingTemplate = true;
        state.error = null;
      })
      .addCase(baixarModeloCsv.fulfilled, (state) => {
        state.isDownloadingTemplate = false;
      })
      .addCase(baixarModeloCsv.rejected, (state, action) => {
        state.isDownloadingTemplate = false;
        state.error = action.payload || 'Erro ao baixar modelo';
      })

      .addCase(criarLote.pending, (state) => {
        state.isUploading = true;
        state.error = null;
        state.uploadErrors = null;
        state.preValidationIssues = null;
        state.preValidationHasCritical = false;
        state.preValidationTotalRows = null;
      })
      .addCase(criarLote.fulfilled, (state, action) => {
        state.isUploading = false;
        state.loteId = action.payload.loteId;
        state.validationResult = null;
      })
      .addCase(criarLote.rejected, (state, action) => {
        state.isUploading = false;
        state.error = action.payload?.message || 'Erro no upload';
        state.uploadErrors = action.payload?.errorsSample || null;
        if (action.payload?.code === 'SPREADSHEET_VALIDATION_FAILED') {
          state.preValidationIssues = action.payload.issues ?? [];
          state.preValidationHasCritical = action.payload.hasCritical ?? true;
          state.preValidationTotalRows = action.payload.totalRows ?? null;
        } else {
          state.preValidationIssues = null;
          state.preValidationHasCritical = false;
          state.preValidationTotalRows = null;
        }
      })
      .addCase(criarLoteComMapeamento.pending, (state) => {
        state.isUploading = true;
        state.error = null;
        state.uploadErrors = null;
        state.preValidationIssues = null;
        state.preValidationHasCritical = false;
        state.preValidationTotalRows = null;
      })
      .addCase(criarLoteComMapeamento.fulfilled, (state, action) => {
        state.isUploading = false;
        state.loteId = action.payload.loteId;
        state.validationResult = null;
      })
      .addCase(criarLoteComMapeamento.rejected, (state, action) => {
        state.isUploading = false;
        state.error = action.payload?.message || 'Erro no upload';
        state.uploadErrors = action.payload?.errorsSample || null;
        if (action.payload?.code === 'SPREADSHEET_VALIDATION_FAILED') {
          state.preValidationIssues = action.payload.issues ?? [];
          state.preValidationHasCritical = action.payload.hasCritical ?? true;
          state.preValidationTotalRows = action.payload.totalRows ?? null;
        } else {
          state.preValidationIssues = null;
          state.preValidationHasCritical = false;
          state.preValidationTotalRows = null;
        }
      })

      .addCase(listarAjustes.pending, (state) => {
        state.isLoadingAjustes = true;
        state.ajustesError = null;
      })
      .addCase(listarAjustes.fulfilled, (state, action) => {
        state.isLoadingAjustes = false;
        state.ajustesError = null;
        state.ajustesGrupos = action.payload;
        state.ajustesEscopo = action.meta.arg ?? null;
      })
      .addCase(listarAjustes.rejected, (state, action) => {
        state.isLoadingAjustes = false;
        state.ajustesError = action.payload || 'Erro ao carregar os ajustes';
      })

      .addCase(listarLotes.pending, (state, action) => {
        state.isLoadingLotes = true;
        state.lotesError = null;
        state.lotesRequestId = action.meta.requestId;
      })
      .addCase(listarLotes.fulfilled, (state, action) => {
        if (state.lotesRequestId !== action.meta.requestId) return;
        state.isLoadingLotes = false;
        state.lotesError = null;
        state.lotes = action.payload.lotes;
        state.lotesDashboard = action.payload.dashboard;
        const pag = action.payload.pagination;
        state.lotesPagination = {
          total: pag.total,
          page: pag.page,
          pageSize: pag.limit,
          totalPages: pag.totalPages,
          hasNext: pag.page < pag.totalPages,
          hasPrevious: pag.page > 1,
        };
      })
      .addCase(listarLotes.rejected, (state, action) => {
        if (state.lotesRequestId !== action.meta.requestId) return;
        state.isLoadingLotes = false;
        state.lotesError = action.payload || 'Erro ao listar lotes';
      })

      .addCase(listarLotesRascunho.pending, (state) => {
        state.isLoadingLotesRascunho = true;
        state.lotesRascunhoError = null;
      })
      .addCase(listarLotesRascunho.fulfilled, (state, action) => {
        state.isLoadingLotesRascunho = false;
        state.lotesRascunho = action.payload.lotes;
        state.totalLotesRascunho = action.payload.total;
      })
      .addCase(listarLotesRascunho.rejected, (state, action) => {
        state.isLoadingLotesRascunho = false;
        state.lotesRascunhoError = action.payload || 'Erro ao carregar rascunhos';
      })

      .addCase(contarLotesRascunho.fulfilled, (state, action) => {
        state.totalLotesRascunho = action.payload;
      })

      .addCase(obterLote.pending, (state) => {
        state.isLoadingLote = true;
        state.error = null;
        state.lotePerformance = null;
        state.isLoadingLotePerformance = false;
      })
      .addCase(obterLote.fulfilled, (state, action) => {
        state.isLoadingLote = false;
        if (state.loteId !== action.payload.lote.id) {
          aplicarConfiguracaoDoLote(state, action.payload.lote);
        }
        state.loteDetail = action.payload;
        state.loteAtual = action.payload.lote;
        state.loteId = action.payload.lote.id;
      })
      .addCase(obterLote.rejected, (state, action) => {
        state.isLoadingLote = false;
        state.error = action.payload || 'Erro ao obter lote';
      })

      .addCase(obterLotePerformance.pending, (state) => {
        state.isLoadingLotePerformance = true;
      })
      .addCase(obterLotePerformance.fulfilled, (state, action) => {
        state.isLoadingLotePerformance = false;
        state.lotePerformance = action.payload;
      })
      .addCase(obterLotePerformance.rejected, (state) => {
        state.isLoadingLotePerformance = false;
        state.lotePerformance = null;
      })

      .addCase(obterLoteSummary.fulfilled, (state, action) => {
        state.loteAtual = action.payload.lote;
      })

      .addCase(obterEstatisticas.pending, (state) => {
        state.isLoadingStats = true;
        state.error = null;
      })
      .addCase(obterEstatisticas.fulfilled, (state, action) => {
        state.isLoadingStats = false;
        state.stats = action.payload;
      })
      .addCase(obterEstatisticas.rejected, (state, action) => {
        state.isLoadingStats = false;
        state.error = action.payload || 'Erro ao obter estatísticas';
      })

      .addCase(listarItensDoLote.pending, (state) => {
        state.isLoadingItens = true;
        state.error = null;
        state.itensDoLote = [];
        state.itensPagination = null;
      })
      .addCase(listarItensDoLote.fulfilled, (state, action) => {
        state.isLoadingItens = false;
        state.itensDoLote = action.payload.items;
        state.itensPagination = {
          total: action.payload.total,
          page: action.payload.page,
          pageSize: action.payload.pageSize,
          totalPages: action.payload.totalPages,
          hasNext: action.payload.hasNext,
          hasPrevious: action.payload.hasPrevious,
        };
      })
      .addCase(listarItensDoLote.rejected, (state, action) => {
        state.isLoadingItens = false;
        state.error = action.payload || 'Erro ao listar itens';
      })

      .addCase(removerItemDaValidacao.pending, (state, action) => {
        const { itemId } = action.meta.arg;
        const item = state.itensDoLote.find((i) => i.id === itemId);

        state.itensDoLote = state.itensDoLote.filter((i) => i.id !== itemId);

        if (state.itensPagination) {
          const nextTotal = Math.max(0, state.itensPagination.total - 1);
          const pageSize = state.itensPagination.pageSize || 1;
          const nextTotalPages = pageSize > 0 ? Math.ceil(nextTotal / pageSize) : 0;
          const page = state.itensPagination.page;

          state.itensPagination = {
            ...state.itensPagination,
            total: nextTotal,
            totalPages: nextTotalPages,
            hasNext: page < nextTotalPages,
            hasPrevious: page > 1,
          };
        }

        if (state.loteAtual?.totais && item) {
          const totais = state.loteAtual.totais;
          totais.totalItens = Math.max(0, totais.totalItens - 1);

          if (item.classificacao === ClassificacaoItem.INVALIDA) {
            totais.invalidos = Math.max(0, totais.invalidos - 1);
          }

          if (item.classificacao === ClassificacaoItem.VALIDA_DIGITAL) {
            totais.validos = Math.max(0, totais.validos - 1);
            totais.digitais = Math.max(0, totais.digitais - 1);
          }

          if (item.classificacao === ClassificacaoItem.VALIDA_CORREIO) {
            totais.validos = Math.max(0, totais.validos - 1);
            totais.fisicos = Math.max(0, totais.fisicos - 1);
          }
        }
      })
      .addCase(removerItemDaValidacao.fulfilled, (state, action) => {
        if (!state.loteAtual) {
          state.loteAtual = action.payload.lote;
          return;
        }

        const currentTotais = state.loteAtual.totais;
        state.loteAtual = {
          ...action.payload.lote,
          totais: currentTotais || action.payload.lote.totais,
        };
      })
      .addCase(removerItemDaValidacao.rejected, (state, action) => {
        state.error = action.payload || 'Erro ao remover item do lote';
      })

      .addCase(listarNotificacoes.pending, (state) => {
        state.isLoadingNotificacoes = true;
        state.notificacoesError = null;
      })
      .addCase(listarNotificacoes.fulfilled, (state, action) => {
        state.isLoadingNotificacoes = false;
        state.notificacoes = action.payload.items;
        state.notificacoesPagination = {
          total: action.payload.total,
          page: action.payload.page,
          pageSize: action.payload.pageSize,
          totalPages: action.payload.totalPages,
          hasNext: action.payload.hasNext,
          hasPrevious: action.payload.hasPrevious,
        };
      })
      .addCase(listarNotificacoes.rejected, (state, action) => {
        state.isLoadingNotificacoes = false;
        state.notificacoesError = action.payload || 'Erro ao listar notificações';
      })

      .addCase(obterNotificacoesStats.pending, (state) => {
        state.isLoadingNotificacoesStats = true;
        state.error = null;
      })
      .addCase(obterNotificacoesStats.fulfilled, (state, action) => {
        state.isLoadingNotificacoesStats = false;
        state.notificacoesStats = action.payload;
      })
      .addCase(obterNotificacoesStats.rejected, (state, action) => {
        state.isLoadingNotificacoesStats = false;
        state.error = action.payload || 'Erro ao obter estatísticas de notificações';
      })

      .addCase(obterLoteTabCounts.pending, (state) => {
        state.isLoadingLoteTabCounts = true;
      })
      .addCase(obterLoteTabCounts.fulfilled, (state, action) => {
        state.isLoadingLoteTabCounts = false;
        state.loteTabCounts = action.payload;
      })
      .addCase(obterLoteTabCounts.rejected, (state) => {
        state.isLoadingLoteTabCounts = false;
      })

      .addCase(obterEntregasPorCanal.pending, (state) => {
        state.entregasPanel = { data: null, isLoading: true, error: false };
      })
      .addCase(obterEntregasPorCanal.fulfilled, (state, action) => {
        state.entregasPanel = { data: action.payload, isLoading: false, error: false };
      })
      .addCase(obterEntregasPorCanal.rejected, (state) => {
        state.entregasPanel = { data: null, isLoading: false, error: true };
      })

      .addCase(obterItem.pending, (state) => {
        state.isLoadingItem = true;
        state.error = null;
        state.itemAtual = null;
      })
      .addCase(obterItem.fulfilled, (state, action) => {
        state.isLoadingItem = false;
        state.itemAtual = action.payload;
      })
      .addCase(obterItem.rejected, (state, action) => {
        state.isLoadingItem = false;
        state.itemAtual = null;
        state.error = action.payload || 'Erro ao obter item';
      })

      .addCase(obterItemPorId.pending, (state) => {
        state.isLoadingItem = true;
        state.error = null;
      })
      .addCase(obterItemPorId.fulfilled, (state, action) => {
        state.isLoadingItem = false;
        state.itemAtual = action.payload;
      })
      .addCase(obterItemPorId.rejected, (state, action) => {
        state.isLoadingItem = false;
        state.error = action.payload || 'Erro ao obter item pelo ID';
      })

      .addCase(obterSnapshotEnvioDigital.pending, (state) => {
        state.isLoadingDigitalSendSnapshot = true;
        state.error = null;
      })
      .addCase(obterSnapshotEnvioDigital.fulfilled, (state, action) => {
        state.isLoadingDigitalSendSnapshot = false;
        state.digitalSendSnapshot = action.payload;
      })
      .addCase(obterSnapshotEnvioDigital.rejected, (state, action) => {
        state.isLoadingDigitalSendSnapshot = false;
        state.digitalSendSnapshot = null;
        state.error = action.payload || 'Erro ao obter snapshot do envio digital';
      })

      .addCase(enviarDigital.pending, (state) => {
        state.isEnviando = true;
        state.error = null;
      })
      .addCase(enviarDigital.fulfilled, (state) => {
        state.isEnviando = false;
      })
      .addCase(enviarDigital.rejected, (state, action) => {
        state.isEnviando = false;
        state.error = action.payload || 'Erro ao enviar digitalmente';
      })

      .addCase(enviarFisico.pending, (state) => {
        state.isEnviando = true;
        state.error = null;
      })
      .addCase(enviarFisico.fulfilled, (state) => {
        state.isEnviando = false;
      })
      .addCase(enviarFisico.rejected, (state, action) => {
        state.isEnviando = false;
        state.error = action.payload || 'Erro ao enviar fisicamente';
      })

      .addCase(reenviarItem.pending, (state) => {
        state.isEnviando = true;
        state.error = null;
      })
      .addCase(reenviarItem.fulfilled, (state) => {
        state.isEnviando = false;
      })
      .addCase(reenviarItem.rejected, (state, action) => {
        state.isEnviando = false;
        state.error = action.payload || 'Erro ao reenviar notificação';
      })

      .addCase(reenviarLote.pending, (state) => {
        state.isEnviando = true;
        state.error = null;
      })
      .addCase(reenviarLote.fulfilled, (state) => {
        state.isEnviando = false;
      })
      .addCase(reenviarLote.rejected, (state, action) => {
        state.isEnviando = false;
        state.error = action.payload || 'Erro ao reenviar lote';
      })

      .addCase(agendarLote.pending, (state) => {
        state.isAgendando = true;
        state.error = null;
      })
      .addCase(agendarLote.fulfilled, (state) => {
        state.isAgendando = false;
      })
      .addCase(agendarLote.rejected, (state, action) => {
        state.isAgendando = false;
        state.error = action.payload || 'Erro ao agendar lote';
      })

      .addCase(confirmarEnvio.pending, (state) => {
        state.isAgendando = true;
        state.error = null;
      })
      .addCase(confirmarEnvio.fulfilled, (state) => {
        state.isAgendando = false;
      })
      .addCase(confirmarEnvio.rejected, (state, action) => {
        state.isAgendando = false;
        state.error = action.payload || 'Erro ao confirmar envio';
      })

      .addCase(validarParaEnvio.pending, (state) => {
        state.isLoadingLote = true;
        state.error = null;
      })
      .addCase(validarParaEnvio.fulfilled, (state) => {
        state.isLoadingLote = false;
      })
      .addCase(validarParaEnvio.rejected, (state, action) => {
        state.isLoadingLote = false;
        state.error = action.payload || 'Erro ao validar lote';
      })

      .addCase(calcularCustos.pending, (state) => {
        state.isLoadingLote = true;
        state.error = null;
      })
      .addCase(calcularCustos.fulfilled, (state) => {
        state.isLoadingLote = false;
      })
      .addCase(calcularCustos.rejected, (state, action) => {
        state.isLoadingLote = false;
        state.error = action.payload || 'Erro ao calcular custos';
      })

      .addCase(cancelarLote.pending, (state) => {
        state.isCancelando = true;
        state.error = null;
      })
      .addCase(cancelarLote.fulfilled, (state) => {
        state.isCancelando = false;
      })
      .addCase(cancelarLote.rejected, (state, action) => {
        state.isCancelando = false;
        state.error = action.payload || 'Erro ao cancelar lote';
      })

      .addCase(enviarLoteAgora.pending, (state) => {
        state.isEnviando = true;
        state.error = null;
      })
      .addCase(enviarLoteAgora.fulfilled, (state) => {
        state.isEnviando = false;
      })
      .addCase(enviarLoteAgora.rejected, (state, action) => {
        state.isEnviando = false;
        state.error = action.payload || 'Erro ao enviar lote';
      })

      .addCase(obterTempoRestanteLote.pending, (state) => {
        state.isLoadingSchedulingInfo = true;
        state.error = null;
      })
      .addCase(obterTempoRestanteLote.fulfilled, (state, action) => {
        state.isLoadingSchedulingInfo = false;
        state.schedulingInfo = action.payload;
      })
      .addCase(obterTempoRestanteLote.rejected, (state, action) => {
        state.isLoadingSchedulingInfo = false;
        state.error = action.payload || 'Erro ao obter tempo restante';
      })

      .addCase(cancelarItem.pending, (state) => {
        state.isCancelando = true;
        state.error = null;
      })
      .addCase(cancelarItem.fulfilled, (state) => {
        state.isCancelando = false;
      })
      .addCase(cancelarItem.rejected, (state, action) => {
        state.isCancelando = false;
        state.error = action.payload || 'Erro ao cancelar item';
      })

      .addCase(encerrarItem.pending, (state) => {
        state.isEncerrando = true;
        state.error = null;
      })
      .addCase(encerrarItem.fulfilled, (state) => {
        state.isEncerrando = false;
      })
      .addCase(encerrarItem.rejected, (state, action) => {
        state.isEncerrando = false;
        state.error = action.payload || 'Erro ao encerrar notificação';
      })

      .addCase(arquivarItem.pending, (state) => {
        state.isArquivando = true;
        state.error = null;
      })
      .addCase(arquivarItem.fulfilled, (state) => {
        state.isArquivando = false;
      })
      .addCase(arquivarItem.rejected, (state, action) => {
        state.isArquivando = false;
        state.error = action.payload || 'Erro ao arquivar item';
      })

      .addCase(restaurarItem.pending, (state) => {
        state.isRestaurando = true;
        state.error = null;
      })
      .addCase(restaurarItem.fulfilled, (state) => {
        state.isRestaurando = false;
      })
      .addCase(restaurarItem.rejected, (state, action) => {
        state.isRestaurando = false;
        state.error = action.payload || 'Erro ao restaurar item';
      })

      .addCase(abrirNegociacaoEmLote.pending, (state) => {
        state.isAbrindoNegociacaoEmLote = true;
        state.error = null;
        state.negociacaoEmLoteResult = null;
      })
      .addCase(abrirNegociacaoEmLote.fulfilled, (state, action) => {
        state.isAbrindoNegociacaoEmLote = false;
        state.negociacaoEmLoteResult = action.payload;
      })
      .addCase(abrirNegociacaoEmLote.rejected, (state, action) => {
        state.isAbrindoNegociacaoEmLote = false;
        state.error = action.payload || 'Erro ao iniciar negociações em lote';
      })

      .addCase(obterInfoCancelamentoItem.pending, (state) => {
        state.isLoadingSchedulingInfo = true;
        state.error = null;
      })
      .addCase(obterInfoCancelamentoItem.fulfilled, (state, action) => {
        state.isLoadingSchedulingInfo = false;
        state.schedulingInfo = action.payload;
      })
      .addCase(obterInfoCancelamentoItem.rejected, (state, action) => {
        state.isLoadingSchedulingInfo = false;
        state.error = action.payload || 'Erro ao obter info de cancelamento';
      })

      .addCase(obterValoresEfetivos.pending, (state) => {
        state.isLoadingValores = true;
        state.error = null;
      })
      .addCase(obterValoresEfetivos.fulfilled, (state, action) => {
        state.isLoadingValores = false;
        state.valoresEfetivos = action.payload;
      })
      .addCase(obterValoresEfetivos.rejected, (state, action) => {
        state.isLoadingValores = false;
        state.error = action.payload || 'Erro ao obter valores';
      })

      .addCase(calcularCustosLote.pending, (state) => {
        state.isLoadingCustos = true;
        state.error = null;
      })
      .addCase(calcularCustosLote.fulfilled, (state, action) => {
        state.isLoadingCustos = false;
        state.custosLote = action.payload;
      })
      .addCase(calcularCustosLote.rejected, (state, action) => {
        state.isLoadingCustos = false;
        state.error = action.payload || 'Erro ao calcular custos';
      })

      .addCase(criarLoteRascunho.pending, (state) => {
        state.isCreatingDraft = true;
        state.error = null;
      })
      .addCase(criarLoteRascunho.fulfilled, (state, action) => {
        state.isCreatingDraft = false;
        state.loteId = action.payload.id;
        state.loteAtual = null;
        state.form.batchCode = action.payload.batchCode;
      })
      .addCase(criarLoteRascunho.rejected, (state, action) => {
        state.isCreatingDraft = false;
        state.error = action.payload || 'Erro ao criar rascunho';
      })

      .addCase(criarIndividual.pending, (state) => {
        state.isCreatingDraft = true;
        state.error = null;
      })
      .addCase(criarIndividual.fulfilled, (state) => {
        state.isCreatingDraft = false;
      })
      .addCase(criarIndividual.rejected, (state, action) => {
        state.isCreatingDraft = false;
        state.error = action.payload?.message || 'Erro ao criar notificação';
      })

      .addCase(atualizarConfiguracaoLote.pending, (state) => {
        state.isSavingConfig = true;
        state.error = null;
      })
      .addCase(atualizarConfiguracaoLote.fulfilled, (state, action) => {
        state.isSavingConfig = false;
        state.loteAtual = action.payload;
        aplicarConfiguracaoDoLote(state, action.payload);
      })
      .addCase(atualizarConfiguracaoLote.rejected, (state, action) => {
        state.isSavingConfig = false;
        state.error = action.payload || 'Erro ao salvar configuração';
      })

      .addCase(avancarConfiguracaoLote.pending, (state) => {
        state.isAdvancing = true;
        state.error = null;
      })
      .addCase(avancarConfiguracaoLote.fulfilled, (state, action) => {
        state.isAdvancing = false;
        state.loteAtual = action.payload;
        aplicarConfiguracaoDoLote(state, action.payload);
        state.currentStep = 'upload';
      })
      .addCase(avancarConfiguracaoLote.rejected, (state, action) => {
        state.isAdvancing = false;
        state.error = action.payload || 'Erro ao avançar configuração';
      })

      .addCase(cancelarConfiguracaoLote.fulfilled, (state) => {
        state.loteId = null;
        state.loteAtual = null;
        state.validationResult = null;
        state.uploadErrors = null;
        state.itensDoLote = [];
        state.itensPagination = null;
      })

      .addCase(obterMetodosPermitidos.pending, (state) => {
        state.isLoadingMetodos = true;
      })
      .addCase(obterMetodosPermitidos.fulfilled, (state, action) => {
        state.isLoadingMetodos = false;
        state.metodosPermitidos = action.payload;
      })
      .addCase(obterMetodosPermitidos.rejected, (state) => {
        state.isLoadingMetodos = false;
        state.metodosPermitidos = [
          'DIGITAL',
          'SEDEX',
          'DIGITAL_SEDEX',
          'EMAIL_APENAS',
          'WHATSAPP_APENAS',
        ];
      })

      .addCase(validarCpfCnpj.pending, (state, action) => {
        state.isValidatingDoc = true;
        state.error = null;
        state.docLookupError = null;
        state.docValidationData = null;
        state.docLookupCurrent = action.meta.arg;
      })
      .addCase(validarCpfCnpj.fulfilled, (state, action) => {
        if (state.docLookupCurrent !== action.meta.arg) {
          return;
        }
        state.isValidatingDoc = false;
        state.docLookupError = null;
        state.docValidationData = action.payload;
        state.docLookupCurrent = null;
      })
      .addCase(validarCpfCnpj.rejected, (state, action) => {
        if (state.docLookupCurrent !== action.meta.arg) {
          return;
        }
        state.isValidatingDoc = false;
        state.docLookupError = action.payload || 'Erro ao validar documento';
        state.error = action.payload || 'Erro ao validar documento';
        state.docLookupCurrent = null;
      });

    builder
      .addCase(obterManifestacaoChat.pending, (state, action) => {
        reiniciarManifestacaoChat(state, action.meta.arg, action.meta.requestId);
        state.isLoadingManifestacaoChat = true;
      })
      .addCase(obterManifestacaoChat.fulfilled, (state, action) => {
        if (state.manifestacaoChatRequestId !== action.meta.requestId) return;
        state.isLoadingManifestacaoChat = false;
        state.manifestacaoChat = chatDaPrimeiraPagina(action.payload);
      })
      .addCase(obterManifestacaoChat.rejected, (state, action) => {
        if (state.manifestacaoChatRequestId !== action.meta.requestId) return;
        state.isLoadingManifestacaoChat = false;
        state.erroManifestacaoChat = action.payload ?? MENSAGEM_DE_FALHA_DO_CHAT;
      });

    builder
      .addCase(obterManifestacaoChatAnteriores.pending, (state, action) => {
        if (!chatDoItem(state, action.meta.arg.itemId)) return;
        state.manifestacaoChatAnterioresRequestId = action.meta.requestId;
        state.isCarregandoAnterioresManifestacaoChat = true;
        state.erroAnterioresManifestacaoChat = null;
      })
      .addCase(obterManifestacaoChatAnteriores.fulfilled, (state, action) => {
        if (state.manifestacaoChatAnterioresRequestId !== action.meta.requestId) return;
        state.manifestacaoChatAnterioresRequestId = null;
        state.isCarregandoAnterioresManifestacaoChat = false;
        const chat = chatDoItem(state, action.meta.arg.itemId);
        if (!chat || action.meta.arg.page !== chat.paginaCarregada + 1) return;
        prependerAnteriores(chat, action.meta.arg.page, action.payload);
      })
      .addCase(obterManifestacaoChatAnteriores.rejected, (state, action) => {
        if (state.manifestacaoChatAnterioresRequestId !== action.meta.requestId) return;
        state.manifestacaoChatAnterioresRequestId = null;
        state.isCarregandoAnterioresManifestacaoChat = false;
        state.erroAnterioresManifestacaoChat = action.payload ?? MENSAGEM_DE_FALHA_DO_CHAT;
      });

    builder
      .addCase(enviarRespostaSimples.pending, (state, action) => {
        state.isEnviandoRespostaSimples = true;
        const chat = chatDoItem(state, action.meta.arg.itemId);
        if (!chat) return;
        chat.mensagens.push({
          id: idTemporario(action.meta.requestId),
          itemId: action.meta.arg.itemId,
          usuarioId: '',
          tipo: 'RESPOSTA_SIMPLES',
          direcao: 'OUTBOUND',
          conteudo: action.meta.arg.conteudo,
          lido: false,
          lidoAt: null,
          dataCriacao: new Date().toISOString(),
          autor: null,
          origem: null,
          opcaoEscolhida: null,
        });
        chat.respostasSimplesSent += 1;
        atualizarPodeResponder(chat);
      })
      .addCase(enviarRespostaSimples.fulfilled, (state, action) => {
        state.isEnviandoRespostaSimples = false;
        const chat = chatDoItem(state, action.meta.arg.itemId);
        if (!chat) return;
        const tempIndex = chat.mensagens.findIndex(
          (m) => m.id === idTemporario(action.meta.requestId),
        );
        const jaCarregada = chat.mensagens.some((m) => m.id === action.payload.id);
        if (tempIndex >= 0 && jaCarregada) chat.mensagens.splice(tempIndex, 1);
        else if (tempIndex >= 0) chat.mensagens[tempIndex] = action.payload;
        else if (!jaCarregada) chat.mensagens.push(action.payload);
      })
      .addCase(enviarRespostaSimples.rejected, (state, action) => {
        state.isEnviandoRespostaSimples = false;
        // O core aceitou o POST: tirar a mensagem e devolver a cota convidaria a reenviar uma
        // resposta jurídica. Quem acerta o chat é a primeira página relida pelo thunk, e não a
        // ordem em que as ações chegam.
        if (action.payload?.motivo === 'leitura_apos_envio') return;
        const chat = chatDoItem(state, action.meta.arg.itemId);
        if (!chat) return;
        const tempIndex = chat.mensagens.findIndex(
          (m) => m.id === idTemporario(action.meta.requestId),
        );
        if (tempIndex < 0) return;
        chat.mensagens.splice(tempIndex, 1);
        chat.respostasSimplesSent = Math.max(0, chat.respostasSimplesSent - 1);
        atualizarPodeResponder(chat);
      });

    builder.addCase(marcarManifestacaoLida.fulfilled, () => {});
  },
});

export const {
  resetForm,
  iniciarNovaNotificacao,
  setBatchName,
  setSendMethod,
  setScheduleMode,
  setScheduledDate,
  setScheduledTime,
  setWhatsappTemplateId,
  setEmailTemplateId,
  setFisicoTemplateId,
  setLotePrazoDias,
  goToStep,
  clearError,
  clearItemEditData,
  clearDigitalSendSnapshot,
  clearManifestacaoChat,
  clearDocValidationData,
  clearPreValidation,
  updateNotificacaoStatus,
} = extrajudicialSlice.actions;

const extrajudicialReducer = extrajudicialSlice.reducer;

export default extrajudicialReducer;
